import React, { useRef, useState } from 'react';
import { Check, Copy, Info, RotateCcw } from 'lucide-react';
import { copyText } from './utils/clipboard';

const UNITS = {
  mad: { symbol: 'DH', name: 'Moroccan Dirham', short: 'MAD', perMad: 1, decimals: 2 },
  ryal: { symbol: 'Ry', name: 'Ryal', short: 'Ryal', perMad: 20, decimals: 0 },
  franc: { symbol: 'Fr', name: 'Franc', short: 'Franc', perMad: 100, decimals: 0 },
};

const EMPTY_VALUES = { mad: '', ryal: '', franc: '' };
const EXAMPLES = [
  { unit: 'mad', value: '10', label: '10 MAD' },
  { unit: 'ryal', value: '100', label: '100 Ryal' },
  { unit: 'franc', value: '1000', label: '1,000 Franc' },
];

const sanitizeAmount = value => {
  const normalized = value.replace(',', '.').replace(/[^\d.]/g, '');
  const [whole = '', ...decimals] = normalized.split('.');
  return decimals.length ? `${whole}.${decimals.join('')}` : whole;
};

const formatUnit = (value, unit) => value.toLocaleString('en-MA', {
  maximumFractionDigits: UNITS[unit].decimals,
});

export default function GrandmaCounter() {
  const [values, setValues] = useState(EMPTY_VALUES);
  const [activeUnit, setActiveUnit] = useState(null);
  const [copiedUnit, setCopiedUnit] = useState('');
  const inputRefs = useRef({});

  const updateFrom = (unit, rawValue) => {
    const cleanValue = sanitizeAmount(rawValue);
    setActiveUnit(unit);
    setCopiedUnit('');

    if (cleanValue === '') {
      setValues(EMPTY_VALUES);
      return;
    }

    const amount = Number.parseFloat(cleanValue);
    if (!Number.isFinite(amount)) return;
    const madValue = amount / UNITS[unit].perMad;

    setValues(Object.fromEntries(
      Object.keys(UNITS).map(key => [key, key === unit ? cleanValue : formatUnit(madValue * UNITS[key].perMad, key)])
    ));
  };

  const copyUnit = async unit => {
    if (!values[unit]) return;
    try {
      await copyText(`${values[unit]} ${UNITS[unit].short}`);
      setCopiedUnit(unit);
      window.setTimeout(() => setCopiedUnit(''), 1800);
    } catch (_) {
      setCopiedUnit('');
    }
  };

  const applyExample = example => {
    updateFrom(example.unit, example.value);
    window.requestAnimationFrame(() => inputRefs.current[example.unit]?.focus({ preventScroll: true }));
  };

  return (
    <section className="page-shell units-page" aria-labelledby="moroccan-units-title">
      <div className="page-heading">
        <div className="eyebrow">Moroccan unit converter</div>
        <h1 id="moroccan-units-title">Speak money like a local.</h1>
        <p>Someone quoted you in Ryal or Franc? Type it exactly as you heard it and get the Dirham value instantly.</p>
      </div>

      <div className="units-panel">
        <div className="units-panel-heading">
          <div>
            <span className="section-label">Moroccan unit conversion</span>
            <h2>Type in any box</h2>
          </div>
          <button
            type="button"
            className="clear-button"
            onClick={() => {
              setValues(EMPTY_VALUES);
              setActiveUnit(null);
            }}
            disabled={!Object.values(values).some(Boolean)}
          >
            <RotateCcw size={14} /> Clear
          </button>
        </div>

        <div className="unit-input-grid">
          {Object.entries(UNITS).map(([key, unit]) => (
            <div className={activeUnit === key ? 'unit-input-card active' : 'unit-input-card'} key={key}>
              <div className="unit-input-heading">
                <label htmlFor={`unit-${key}`}>
                  <span>{unit.name}</span>
                  <small>{unit.short}</small>
                </label>
                <button type="button" onClick={() => copyUnit(key)} disabled={!values[key]} aria-label={`Copy ${unit.name} value`}>
                  {copiedUnit === key ? <Check size={15} /> : <Copy size={15} />}
                </button>
              </div>
              <div className="unit-input-control">
                <span>{unit.symbol}</span>
                <input
                  id={`unit-${key}`}
                  ref={element => { inputRefs.current[key] = element; }}
                  aria-label={unit.name}
                  inputMode="decimal"
                  autoComplete="off"
                  value={values[key]}
                  onFocus={() => setActiveUnit(key)}
                  onBlur={() => setActiveUnit(null)}
                  onChange={event => updateFrom(key, event.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="unit-examples">
          <span>Try an example</span>
          <div role="group" aria-label="Example amounts">
            {EXAMPLES.map(example => (
              <button
                key={example.label}
                type="button"
                aria-pressed={values[example.unit] === example.value}
                onClick={() => applyExample(example)}
              >
                {example.label}
              </button>
            ))}
          </div>
        </div>

        <div className="unit-facts" aria-label="Moroccan unit equivalences">
          <div className="unit-fact">
            <span>Ryal</span>
            <strong>1 MAD = 20 Ryal</strong>
            <small>Common in everyday counting.</small>
          </div>
          <div className="unit-fact">
            <span>Franc</span>
            <strong>1 MAD = 100 Franc</strong>
            <small>A familiar spoken convention.</small>
          </div>
        </div>

        <details className="unit-convention">
          <summary><Info size={16} /> About regional conventions</summary>
          <p>This tool uses 1 MAD = 20 Ryal = 100 Franc. Colloquial usage can vary by region and context.</p>
        </details>

        <p className="unit-culture-note">Different words. Same money. More confident conversations.</p>
      </div>
      <span className="sr-only" aria-live="polite">{copiedUnit ? 'Value copied to clipboard' : ''}</span>
    </section>
  );
}
